(function() {
  class SalesProductModal extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.sectionId = this.dataset.sectionId;
      this.dialog = this.querySelector('[data-sales-product-modal-dialog]');
      this.body = this.querySelector('[data-sales-product-modal-body]');
      this.closeButton = this.querySelector('[data-sales-product-modal-close]');
      this.previousFocus = null;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);

      document.addEventListener('click', this.handleDocumentClick);

      if (this.dialog) {
        this.dialog.addEventListener('click', this.handleDialogClick);
        this.dialog.addEventListener('close', this.handleDialogClose);
      }

      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => this.close());
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);

      if (this.dialog) {
        this.dialog.removeEventListener('click', this.handleDialogClick);
        this.dialog.removeEventListener('close', this.handleDialogClose);
      }
    }

    handleDocumentClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const trigger = target.closest('[data-sales-product-modal-trigger]');
      if (!trigger || trigger.dataset.salesModalSection !== this.sectionId) return;

      event.preventDefault();
      this.open(trigger.dataset.salesProductId, trigger.href, trigger.dataset.salesVariantId);
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) {
        this.close();
      }
    }

    handleDialogClose() {
      document.documentElement.classList.remove('sales-product-modal-open');

      if (this.body) {
        this.body.innerHTML = '';
      }

      if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
        this.previousFocus.focus({ preventScroll: true });
      }

      this.previousFocus = null;
      this.dialog.classList.remove('dialog-closing');
    }

    open(productId, fallbackUrl, preselectVariantId) {
      if (!this.dialog || !this.body || !productId) {
        if (fallbackUrl) window.location.href = fallbackUrl;
        return;
      }

      const template = document.getElementById('sales-product-modal-template-' + this.sectionId + '-' + productId);

      if (!template) {
        if (fallbackUrl) window.location.href = fallbackUrl;
        return;
      }

      if (this.dialog.open) {
        this.dialog.close();
      }

      this.previousFocus = document.activeElement;
      this.body.innerHTML = '';
      this.body.appendChild(template.content.cloneNode(true));

      const productContent = this.body.querySelector('[data-sales-product-content]');
      if (productContent) {
        this.initProductContent(productContent, preselectVariantId);
      }

      document.documentElement.classList.add('sales-product-modal-open');

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      if (this.closeButton) {
        this.closeButton.focus({ preventScroll: true });
      }
    }

    close() {
      if (!this.dialog || !this.dialog.open) return;

      this.dialog.classList.add('dialog-closing');

      window.setTimeout(() => {
        if (!this.dialog.open) return;

        if (typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
          this.handleDialogClose();
        }
      }, 170);
    }

    initProductContent(root, preselectVariantId) {
      const productId = root.dataset.productId;
      const sectionInstance = root.dataset.sectionInstance;
      const formEl = root.querySelector('.pwc-form');
      const variantInput = root.querySelector('#pwc-variant-id-' + cssEscape(sectionInstance));
      const sellingPlanInput = root.querySelector('#pwc-selling-plan-' + cssEscape(sectionInstance));
      const addBtn = root.querySelector('#pwc-add-btn-' + cssEscape(sectionInstance));
      const btnText = root.querySelector('#pwc-btn-text-' + cssEscape(sectionInstance));
      const priceDisplay = root.querySelector('#pwc-price-display-' + cssEscape(sectionInstance));
      const galleryEl = root.querySelector('#pwc-gallery-' + cssEscape(sectionInstance));
      const thumbnailRailEl = galleryEl ? galleryEl.querySelector('.pwc-thumbnail-rail') : null;
      const thumbnailsEl = galleryEl ? galleryEl.querySelector('.pwc-thumbnails') : null;
      const thumbnailScrollBtnEl = galleryEl ? galleryEl.querySelector('.pwc-thumbnail-scroll-btn') : null;
      const mainImageWrapperEl = galleryEl ? galleryEl.querySelector('.pwc-main-image-wrapper') : null;
      const purchaseButtons = Array.from(root.querySelectorAll('.pwc-purchase-option-toggle[data-purchase-type]'));
      const purchaseOptions = Array.from(root.querySelectorAll('.pwc-purchase-option[data-purchase-option]'));
      const oneTimeCurrent = root.querySelector('[data-pwc-one-time-current]');
      const oneTimeCompare = root.querySelector('[data-pwc-one-time-compare]');
      const subscriptionOption = root.querySelector('[data-purchase-option="subscription"]');
      const subscriptionSavings = root.querySelector('[data-pwc-subscription-savings]');
      const subscriptionCompare = root.querySelector('[data-pwc-subscription-compare]');
      const subscriptionCurrent = root.querySelector('[data-pwc-subscription-current]');
      const subscriptionFrequencyWrap = root.querySelector('[data-pwc-subscription-frequency-wrap]');
      const subscriptionSelect = root.querySelector('[data-pwc-subscription-select]');
      const variants = parseJSONScript(root.querySelector('[data-sales-product-variants]'), []);
      const subscriptionPlanData = parseJSONScript(root.querySelector('[data-sales-product-subscriptions]'), {});
      const hideSubscriptionBox = root.dataset.hideSubscriptionBox === 'true';
      const variantMediaIndexes = new Set(
        variants
          .map(function(variant) {
            return getVariantMediaIndex(variant);
          })
          .filter(function(index) {
            return Number.isInteger(index) && index >= 0;
          })
      );
      const cartAddUrl = window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url
        ? window.Theme.routes.cart_add_url
        : '/cart/add.js';
      const selectedOptions = {};
      let selectedPurchaseType = 'one-time';
      let selectedSubscriptionPlanId = subscriptionOption && subscriptionOption.dataset.sellingPlanId
        ? String(subscriptionOption.dataset.sellingPlanId)
        : null;

      // Promotion (manual discount) preview -- applies to the ONE-TIME price only,
      // and only when the product is flagged eligible (price.manually_discount).
      // Subscription prices are intentionally left unchanged, matching the PDP/cart.
      const promoEligibility = parseJSONScript(root.querySelector('[data-sales-promo-eligibility]'), {});
      const promoPercent = Number(root.dataset.promoDiscountPercent) || 0;
      function applyPromo(cents, variantId) {
        if (promoPercent <= 0 || cents == null) return cents;
        if (!promoEligibility[String(variantId)]) return cents;
        const bounded = Math.min(Math.max(promoPercent, 0), 100);
        return Math.ceil(Number(cents) * (100 - bounded) / 100);
      }

      if (!formEl || !variantInput || !addBtn || !btnText) return;

      root.querySelectorAll('[data-sales-modal-option]').forEach(function(button) {
        const optionIndex = button.dataset.optionIndex;
        if (button.classList.contains('selected') && optionIndex != null) {
          selectedOptions[optionIndex] = button.dataset.value;
        }
      });

      function findVariant() {
        const selectedKeys = Object.keys(selectedOptions);
        const currentVariantId = variantInput.value;

        if (!selectedKeys.length) {
          return variants.find(function(variant) {
            return String(variant.id) === String(currentVariantId);
          }) || variants[0] || null;
        }

        return variants.find(function(variant) {
          const options = [variant.option1, variant.option2, variant.option3];

          return selectedKeys.every(function(optionIndex) {
            return options[Number(optionIndex)] === selectedOptions[optionIndex];
          });
        }) || null;
      }

      function formatMoney(cents) {
        const amount = Number(cents);
        if (!Number.isFinite(amount)) return '';
        return '$' + (amount / 100).toFixed(2);
      }

      function getSubscriptionPlans(variant) {
        if (!variant) return [];
        if (hideSubscriptionBox) return [];

        const variantSubscriptionData = subscriptionPlanData[String(variant.id)];
        if (!variantSubscriptionData || !Array.isArray(variantSubscriptionData.plans)) {
          return [];
        }

        return variantSubscriptionData.plans;
      }

      function getActiveSubscriptionPlan(variant) {
        const plans = getSubscriptionPlans(variant);

        if (!plans.length) {
          selectedSubscriptionPlanId = null;
          return null;
        }

        const matchedPlan = plans.find(function(plan) {
          return String(plan.planId) === String(selectedSubscriptionPlanId);
        });
        const activePlan = matchedPlan || plans[0];
        selectedSubscriptionPlanId = String(activePlan.planId);
        return activePlan;
      }

      function renderSubscriptionSelect(variant, activePlan) {
        if (!subscriptionSelect) return;

        const plans = getSubscriptionPlans(variant);
        subscriptionSelect.innerHTML = '';

        if (!plans.length) {
          subscriptionSelect.disabled = true;
          return;
        }

        plans.forEach(function(plan) {
          const option = document.createElement('option');
          option.value = String(plan.planId);
          option.textContent = plan.label;

          if (activePlan && String(plan.planId) === String(activePlan.planId)) {
            option.selected = true;
          }

          subscriptionSelect.appendChild(option);
        });

        if (activePlan) {
          subscriptionSelect.value = String(activePlan.planId);
        }

        subscriptionSelect.disabled = false;
      }

      function renderPriceDisplay(variant) {
        if (!priceDisplay || !variant) return;

        const subscriptionPlan = getActiveSubscriptionPlan(variant);
        let comparePrice = null;
        let currentPrice = variant.price;

        if (selectedPurchaseType === 'subscription' && subscriptionPlan && subscriptionPlan.price != null) {
          currentPrice = subscriptionPlan.price;
          comparePrice = subscriptionPlan.compareAtPrice;
        } else {
          const discounted = applyPromo(variant.price, variant.id);
          if (discounted < variant.price) {
            currentPrice = discounted;
            comparePrice = variant.price;
          } else if (variant.compare_at_price > variant.price) {
            comparePrice = variant.compare_at_price;
          }
        }

        let priceHtml = '';
        if (comparePrice != null && comparePrice > currentPrice) {
          priceHtml += '<span class="pwc-price-compare">' + formatMoney(comparePrice) + '</span>';
        }
        priceHtml += '<span class="pwc-price-current">' + formatMoney(currentPrice) + '</span>';
        priceDisplay.innerHTML = priceHtml;
      }

      function applyPurchaseType(type, variant) {
        const activeVariant = variant || findVariant();
        const activeSubscriptionPlan = getActiveSubscriptionPlan(activeVariant);
        const hasSubscription = subscriptionOption && !subscriptionOption.hidden && activeSubscriptionPlan;

        if (type === 'subscription' && hasSubscription) {
          selectedPurchaseType = 'subscription';

          if (sellingPlanInput) {
            sellingPlanInput.disabled = false;
            sellingPlanInput.value = String(activeSubscriptionPlan.planId);
          }
        } else {
          selectedPurchaseType = 'one-time';

          if (sellingPlanInput) {
            sellingPlanInput.value = '';
            sellingPlanInput.disabled = true;
          }
        }

        purchaseOptions.forEach(function(option) {
          option.classList.toggle('is-selected', option.dataset.purchaseOption === selectedPurchaseType);
        });

        purchaseButtons.forEach(function(button) {
          button.setAttribute('aria-pressed', String(button.dataset.purchaseType === selectedPurchaseType));
        });

        if (subscriptionFrequencyWrap) {
          subscriptionFrequencyWrap.hidden = !(selectedPurchaseType === 'subscription' && hasSubscription);
        }

        renderPriceDisplay(activeVariant);
      }

      function updateSubscriptionUI(variant) {
        const subscriptionPlans = getSubscriptionPlans(variant);
        const activeSubscriptionPlan = getActiveSubscriptionPlan(variant);

        if (oneTimeCurrent) {
          const oneTimeBase = variant.price;
          const oneTimeDiscounted = applyPromo(oneTimeBase, variant.id);
          oneTimeCurrent.textContent = formatMoney(oneTimeDiscounted);
          if (oneTimeCompare) {
            let oneTimeCompareVal = null;
            if (oneTimeDiscounted < oneTimeBase) {
              oneTimeCompareVal = oneTimeBase;
            } else if (variant.compare_at_price > oneTimeBase) {
              // Regular Compare-at "was" price (a normal sale, no promo).
              oneTimeCompareVal = variant.compare_at_price;
            }
            if (oneTimeCompareVal != null) {
              oneTimeCompare.hidden = false;
              oneTimeCompare.textContent = formatMoney(oneTimeCompareVal);
            } else {
              oneTimeCompare.hidden = true;
              oneTimeCompare.textContent = '';
            }
          }
        }

        if (subscriptionOption) {
          if (subscriptionPlans.length && activeSubscriptionPlan && activeSubscriptionPlan.price != null) {
            subscriptionOption.hidden = false;
            subscriptionOption.dataset.sellingPlanId = String(activeSubscriptionPlan.planId);
            renderSubscriptionSelect(variant, activeSubscriptionPlan);

            if (subscriptionSavings) {
              subscriptionSavings.textContent = activeSubscriptionPlan.savingsPercent ? 'Save ' + activeSubscriptionPlan.savingsPercent + '%' : '';
            }

            if (subscriptionCurrent) {
              subscriptionCurrent.textContent = formatMoney(activeSubscriptionPlan.price);
            }

            if (subscriptionCompare) {
              if (activeSubscriptionPlan.compareAtPrice != null) {
                subscriptionCompare.hidden = false;
                subscriptionCompare.textContent = formatMoney(activeSubscriptionPlan.compareAtPrice);
              } else {
                subscriptionCompare.hidden = true;
                subscriptionCompare.textContent = '';
              }
            }
          } else {
            subscriptionOption.hidden = true;
            subscriptionOption.dataset.sellingPlanId = '';
            renderSubscriptionSelect(variant, null);

            if (subscriptionSavings) subscriptionSavings.textContent = '';
            if (subscriptionCurrent) subscriptionCurrent.textContent = '';

            if (subscriptionCompare) {
              subscriptionCompare.hidden = true;
              subscriptionCompare.textContent = '';
            }

            if (subscriptionFrequencyWrap) {
              subscriptionFrequencyWrap.hidden = true;
            }
          }
        }

        if (selectedPurchaseType === 'subscription' && !activeSubscriptionPlan) {
          applyPurchaseType('one-time', variant);
        } else {
          applyPurchaseType(selectedPurchaseType, variant);
        }
      }

      function setUnavailableState() {
        addBtn.disabled = true;
        addBtn.classList.add('pwc-add-btn--unavailable');
        btnText.textContent = 'Unavailable';
      }

      function getVariantMediaIndex(variant) {
        if (!variant) return null;

        let mediaPosition = null;

        if (variant.featured_media && variant.featured_media.position) {
          mediaPosition = Number(variant.featured_media.position);
        } else if (variant.featured_image && variant.featured_image.position) {
          mediaPosition = Number(variant.featured_image.position);
        }

        if (mediaPosition == null || !Number.isFinite(mediaPosition)) return null;

        const mediaIndex = mediaPosition - 1;
        return Number.isInteger(mediaIndex) && mediaIndex >= 0 ? mediaIndex : null;
      }

      function getVisibleGalleryIndexes() {
        if (!galleryEl) return [];

        return Array.from(galleryEl.querySelectorAll('.pwc-media'))
          .filter(function(mediaEl) {
            return !mediaEl.hidden;
          })
          .map(function(mediaEl) {
            return Number(mediaEl.dataset.index);
          })
          .filter(function(index) {
            return Number.isInteger(index);
          });
      }

      function getVisibleThumbnailButtons() {
        if (!thumbnailsEl) return [];

        return Array.from(thumbnailsEl.querySelectorAll('.pwc-thumb')).filter(function(thumb) {
          return !thumb.hidden;
        });
      }

      function updateThumbnailScrollButton() {
        if (!thumbnailsEl || !thumbnailScrollBtnEl) return;

        if (window.innerWidth < 900) {
          thumbnailScrollBtnEl.hidden = true;
          thumbnailScrollBtnEl.disabled = true;
          return;
        }

        const visibleThumbs = getVisibleThumbnailButtons();
        const lastVisibleThumb = visibleThumbs[visibleThumbs.length - 1];
        const thumbnailsRect = thumbnailsEl.getBoundingClientRect();
        const lastThumbRect = lastVisibleThumb ? lastVisibleThumb.getBoundingClientRect() : null;
        const maxScrollTop = Math.max(0, thumbnailsEl.scrollHeight - thumbnailsEl.clientHeight);
        const hasRemainingScroll = maxScrollTop - thumbnailsEl.scrollTop > 2;
        const hasVisibleThumbBelow = lastVisibleThumb
          ? lastThumbRect.bottom > thumbnailsRect.bottom + 2
          : false;
        const canScrollFurther = hasRemainingScroll && hasVisibleThumbBelow;

        thumbnailScrollBtnEl.hidden = !canScrollFurther;
        thumbnailScrollBtnEl.disabled = !canScrollFurther;
      }

      function syncThumbnailRailHeight() {
        if (!thumbnailRailEl || !mainImageWrapperEl) {
          updateThumbnailScrollButton();
          return;
        }

        if (window.innerWidth < 900) {
          thumbnailRailEl.style.removeProperty('--pwc-gallery-max-height');
          updateThumbnailScrollButton();
          return;
        }

        const mainHeight = mainImageWrapperEl.offsetHeight;
        if (mainHeight > 0) {
          thumbnailRailEl.style.setProperty('--pwc-gallery-max-height', mainHeight + 'px');
        } else {
          thumbnailRailEl.style.removeProperty('--pwc-gallery-max-height');
        }
        updateThumbnailScrollButton();
      }

      function switchGallery(index) {
        if (!galleryEl || index == null || Number.isNaN(index)) return;

        galleryEl.querySelectorAll('.pwc-media').forEach(function(media, mediaIndex) {
          media.classList.toggle('active', mediaIndex === index);
        });

        galleryEl.querySelectorAll('.pwc-thumb').forEach(function(thumb, thumbIndex) {
          thumb.classList.toggle('active', thumbIndex === index);
        });

        const activeThumb = galleryEl.querySelector('.pwc-thumb.active');
        if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
          activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }

        updateThumbnailScrollButton();
      }

      function syncVariantGalleryMedia(variant) {
        if (!galleryEl || !variantMediaIndexes.size) {
          syncThumbnailRailHeight();
          return;
        }

        const activeVariantMediaIndex = getVariantMediaIndex(variant);
        const shouldFilterVariantMedia = activeVariantMediaIndex !== null;
        const galleryItems = Array.from(galleryEl.querySelectorAll('.pwc-media, .pwc-thumb'));

        galleryItems.forEach(function(item) {
          const index = Number(item.dataset.index);
          const isVariantMedia = Number.isInteger(index) && variantMediaIndexes.has(index);
          item.hidden = shouldFilterVariantMedia && isVariantMedia && index !== activeVariantMediaIndex;
        });

        const activeMedia = galleryEl.querySelector('.pwc-media.active');
        if (activeMedia && activeMedia.hidden) {
          const visibleIndexes = getVisibleGalleryIndexes();
          if (visibleIndexes.length) {
            switchGallery(visibleIndexes[0]);
          }
        }

        syncThumbnailRailHeight();
      }

      function getGuideValueMatchKeys(value) {
        const base = String(value || '').trim().toLowerCase();

        if (!base) return [];

        const keys = new Set([
          base,
          base.replace(/\s+/g, ''),
          base.replace(/[^a-z0-9]/g, '')
        ]);

        const withoutParenthetical = base.replace(/\s*\([^)]*\)/g, '').trim();
        if (withoutParenthetical) {
          keys.add(withoutParenthetical);
          keys.add(withoutParenthetical.replace(/\s+/g, ''));
          keys.add(withoutParenthetical.replace(/[^a-z0-9]/g, ''));
        }

        const ounceMatch = base.match(/(\d+(?:\.\d+)?)\s*oz\b/);
        if (ounceMatch) {
          keys.add(ounceMatch[1] + 'oz');
        }

        const firstToken = withoutParenthetical.split(/\s+/)[0];
        if (firstToken) {
          keys.add(firstToken);
        }

        return Array.from(keys);
      }

      function isMatchingGuideValue(candidateValue, guideValue) {
        const candidateKeys = getGuideValueMatchKeys(candidateValue);
        const guideKeys = getGuideValueMatchKeys(guideValue);

        return candidateKeys.some(function(key) {
          return guideKeys.indexOf(key) !== -1;
        });
      }

      function findSalesGuideOptionButton(guideValue, guideKind) {
        const selector = guideKind === 'size'
          ? '.pwc-size-btn[data-sales-modal-option]'
          : '.pwc-swatch-btn[data-sales-modal-option]';
        const buttons = Array.from(root.querySelectorAll(selector));

        return buttons.find(function(button) {
          return isMatchingGuideValue(button.dataset.value, guideValue);
        }) || buttons.find(function(button) {
          const labelEl = button.querySelector('.pwc-size-value, .pwc-swatch-name');
          return isMatchingGuideValue(labelEl ? labelEl.textContent : '', guideValue);
        }) || null;
      }

      function closeSalesGuideDialog(guideDialogEl) {
        const dialog = guideDialogEl ? guideDialogEl.querySelector('dialog') : null;
        if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

        dialog.classList.add('dialog-closing');

        const rawSpeed = getComputedStyle(document.documentElement)
          .getPropertyValue('--animation-speed')
          .trim();
        const duration = rawSpeed ? parseFloat(rawSpeed) * 1000 : 250;

        window.setTimeout(function() {
          dialog.classList.remove('dialog-closing');
          dialog.close();
          document.documentElement.removeAttribute('scroll-lock');
          document.body.style.overflow = '';
        }, duration);
      }

      function openSalesGuideDialog(trigger) {
        const guideKind = trigger.dataset.guideKind || (trigger.hasAttribute('data-size-guide-trigger') ? 'size' : 'scent');
        const triggerProductId = trigger.dataset.productId || productId;
        const guideDialogEl =
          root.querySelector('#' + cssEscape(guideKind + '-guide-dialog-' + triggerProductId)) ||
          document.getElementById(guideKind + '-guide-dialog-' + triggerProductId);

        if (!guideDialogEl) {
          console.warn('[SalesProductModal] Could not find guide dialog for product', {
            guideKind: guideKind,
            productId: triggerProductId
          });
          return;
        }

        if (typeof guideDialogEl.openDialog === 'function') {
          guideDialogEl.openDialog();
          return;
        }

        const dialog = guideDialogEl.querySelector('dialog');
        if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
        document.documentElement.setAttribute('scroll-lock', '');
        document.body.style.overflow = 'hidden';
      }

      function selectSalesGuideOption(selectButton) {
        const guideKind = selectButton.dataset.guideKind || 'scent';
        const guideValue = selectButton.dataset.guideValue || selectButton.dataset.scentValue;
        const optionButton = findSalesGuideOptionButton(guideValue, guideKind);

        if (optionButton && !optionButton.classList.contains('selected')) {
          optionButton.click();
        } else if (!optionButton) {
          console.warn('[SalesProductModal] Could not match guide selection', {
            guideKind: guideKind,
            productId: productId,
            guideValue: guideValue
          });
        }
      }

      function handleSalesGuideClick(event) {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const trigger = target.closest('[data-guide-trigger], [data-scent-guide-trigger], [data-size-guide-trigger]');
        if (trigger && root.contains(trigger)) {
          event.preventDefault();
          event.stopPropagation();
          openSalesGuideDialog(trigger);
          return;
        }

        const closeButton = target.closest('[data-guide-close], [data-scent-guide-close]');
        const closeGuideDialogEl = closeButton ? closeButton.closest('product-guide-dialog') : null;
        if (closeGuideDialogEl && root.contains(closeGuideDialogEl)) {
          event.preventDefault();
          event.stopPropagation();
          closeSalesGuideDialog(closeGuideDialogEl);
          return;
        }

        const selectButton = target.closest('[data-guide-select], [data-scent-guide-select]');
        const selectGuideDialogEl = selectButton ? selectButton.closest('product-guide-dialog') : null;
        if (selectGuideDialogEl && root.contains(selectGuideDialogEl)) {
          event.preventDefault();
          event.stopPropagation();
          selectSalesGuideOption(selectButton);
          closeSalesGuideDialog(selectGuideDialogEl);
          return;
        }

        if (event.target instanceof HTMLDialogElement) {
          const backdropGuideDialogEl = event.target.closest('product-guide-dialog');
          if (backdropGuideDialogEl && root.contains(backdropGuideDialogEl)) {
            event.preventDefault();
            event.stopPropagation();
            closeSalesGuideDialog(backdropGuideDialogEl);
          }
        }
      }

      function handleSalesGuideCancel(event) {
        const dialog = event.target;
        if (!(dialog instanceof HTMLDialogElement)) return;

        const guideDialogEl = dialog.closest('product-guide-dialog');
        if (!guideDialogEl || !root.contains(guideDialogEl)) return;

        event.preventDefault();
        closeSalesGuideDialog(guideDialogEl);
      }

      function updateGalleryFromVariant(variant) {
        if (!variant) return;

        syncVariantGalleryMedia(variant);

        const variantMediaIndex = getVariantMediaIndex(variant);
        if (variantMediaIndex !== null) {
          switchGallery(variantMediaIndex);
        }
      }

      function updateVariant() {
        const variant = findVariant();

        if (!variant) {
          setUnavailableState();
          return;
        }

        variantInput.value = variant.id;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));

        updateSubscriptionUI(variant);

        if (variant.available) {
          addBtn.disabled = false;
          addBtn.classList.remove('pwc-add-btn--unavailable');
          btnText.textContent = addBtn.dataset.defaultText || 'Add to Cart';
        } else {
          addBtn.disabled = true;
          addBtn.classList.add('pwc-add-btn--unavailable');
          btnText.textContent = 'Sold Out';
        }

        updateGalleryFromVariant(variant);
      }

      function openCartDrawer() {
        const cartDrawer =
          document.querySelector('#header-component .wc-header-cart-source cart-drawer-component') ||
          document.querySelector('#header-component cart-drawer-component') ||
          document.querySelector('cart-drawer-component');

        if (!cartDrawer) return;

        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
          return;
        }

        const cartTrigger = cartDrawer.querySelector('[data-testid="cart-drawer-trigger"]');
        if (cartTrigger instanceof HTMLElement) {
          cartTrigger.click();
        }
      }

      function dispatchCartUpdate(response, formData) {
        const quantityAdded = Number(formData.get('quantity')) || 1;
        const variantId = String(formData.get('id') || '');

        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: {
            resource: response || {},
            sourceId: variantId,
            data: {
              source: 'product-form-component',
              itemCount: quantityAdded,
              productId: productId,
              variantId: variantId,
              sections: response && response.sections ? response.sections : {}
            }
          }
        }));
      }

      function removeDuplicateRechargeBlock() {
        const scanRoot = root || formEl.parentElement || formEl;
        if (scanRoot) {
          const selectorCandidates = scanRoot.querySelectorAll(
            '[class*="recharge" i], [id*="recharge" i], [class*="rc-widget" i], [id*="rc-widget" i], [data-recharge], recharge-purchase-options, rc-purchase-options'
          );
          selectorCandidates.forEach((el) => {
            if (el.closest('.pwc-purchase-type, .pwc-option, .pwc-bis-form')) return;
            if (el.contains(formEl) || el.querySelector('.pwc-form, .pwc-purchase-type, .pwc-option, .pwc-gallery, .pwc-info')) return;
            el.remove();
          });
        }

        const containers = [formEl];
        const formParent = formEl.parentElement;
        if (formParent && formParent !== document.body) containers.push(formParent);

        const isOwnUi = (el) => {
          if (!el || !el.classList) return false;
          return el.classList.contains('pwc-option') ||
                 el.classList.contains('pwc-purchase-type') ||
                 el.classList.contains('pwc-purchase-options') ||
                 el.classList.contains('pwc-purchase-option') ||
                 el.classList.contains('pwc-bis-form') ||
                 el.classList.contains('pwc-info') ||
                 el.classList.contains('pwc-bullets') ||
                 el.classList.contains('pwc-title') ||
                 el.classList.contains('pwc-reviews') ||
                 el.classList.contains('pwc-price-wrapper') ||
                 el.classList.contains('pwc-gallery') ||
                 el.classList.contains('pwc-form') ||
                 el.classList.contains('pwc-mobile-sticky-atc') ||
                 el.classList.contains('pwc-add-btn');
        };

        for (const container of containers) {
          const children = Array.from(container.children);
          for (const child of children) {
            if (!(child instanceof HTMLElement)) continue;
            if (child === formEl) continue;
            if (child.matches('input[type="hidden"][name="id"]')) continue;
            if (isOwnUi(child)) continue;
            if (child.querySelector('.pwc-form, .pwc-purchase-type, .pwc-option, .pwc-gallery, .pwc-info')) continue;

            const tag = (child.tagName || '').toLowerCase();
            const text = (child.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
            const className = typeof child.className === 'string' ? child.className.toLowerCase() : '';
            const id = (child.id || '').toLowerCase();

            const looksLikeRecharge =
              tag.startsWith('recharge-') ||
              tag.startsWith('rc-') ||
              className.includes('recharge') ||
              className.includes('rc-widget') ||
              id.includes('recharge') ||
              id.includes('rc-widget') ||
              child.dataset.recharge != null ||
              text.includes('subscription details') ||
              text.includes('subscribe & save') ||
              text.includes('subscribe and save') ||
              (text.includes('one-time purchase') && (text.includes('subscribe') || text.includes('subscription'))) ||
              (text.includes('delivery every') && (text.includes('subscription') || text.includes('subscribe')));

            if (looksLikeRecharge) {
              child.remove();
            }
          }
        }
      }

      btnText.closest('.pwc-add-btn').dataset.defaultText = btnText.textContent.trim();

      root.addEventListener('click', handleSalesGuideClick, true);
      root.querySelectorAll('product-guide-dialog dialog').forEach(function(dialog) {
        dialog.addEventListener('cancel', handleSalesGuideCancel);
      });

      root.querySelectorAll('[data-sales-modal-option]').forEach(function(button) {
        button.addEventListener('click', function() {
          const optionIndex = button.dataset.optionIndex;
          const value = button.dataset.value;
          if (optionIndex == null || value == null) return;

          selectedOptions[optionIndex] = value;

          root.querySelectorAll('[data-sales-modal-option][data-option-index="' + optionIndex + '"]').forEach(function(groupButton) {
            groupButton.classList.toggle('selected', groupButton.dataset.value === value);
          });

          const labelEl = root.querySelector('#pwc-sel-' + cssEscape(sectionInstance) + '-' + optionIndex);
          if (labelEl) {
            labelEl.textContent = value;
          }

          updateVariant();
        });
      });

      if (galleryEl) {
        galleryEl.querySelectorAll('.pwc-thumb').forEach(function(thumb) {
          thumb.addEventListener('click', function() {
            switchGallery(Number(thumb.dataset.index));
          });
        });

        if (thumbnailScrollBtnEl && thumbnailsEl) {
          thumbnailScrollBtnEl.addEventListener('click', function() {
            if (thumbnailScrollBtnEl.disabled) return;
            thumbnailsEl.scrollBy({
              top: Math.max(thumbnailsEl.clientHeight * 0.75, 96),
              behavior: 'smooth'
            });
          });
          thumbnailsEl.addEventListener('scroll', updateThumbnailScrollButton, { passive: true });
        }

        if (mainImageWrapperEl) {
          if (typeof ResizeObserver === 'function') {
            const resizeObserver = new ResizeObserver(syncThumbnailRailHeight);
            resizeObserver.observe(mainImageWrapperEl);
          } else {
            window.addEventListener('resize', syncThumbnailRailHeight);
          }
          galleryEl.querySelectorAll('.pwc-main-img').forEach(function(img) {
            if (img.complete) return;
            img.addEventListener('load', syncThumbnailRailHeight, { once: true });
          });
        }

        syncThumbnailRailHeight();
        window.requestAnimationFrame(syncThumbnailRailHeight);
      }

      purchaseButtons.forEach(function(button) {
        button.addEventListener('click', function() {
          applyPurchaseType(button.dataset.purchaseType);
        });
      });

      if (subscriptionSelect) {
        subscriptionSelect.addEventListener('change', function(event) {
          const select = event.target;
          if (!(select instanceof HTMLSelectElement)) return;

          const activeVariant = findVariant();
          if (!activeVariant) return;

          selectedSubscriptionPlanId = select.value ? String(select.value) : null;
          selectedPurchaseType = 'subscription';
          updateSubscriptionUI(activeVariant);
        });
      }

      formEl.addEventListener('submit', (event) => {
        event.preventDefault();

        if (addBtn.disabled || formEl.dataset.loading === 'true') return;

        const originalButtonText = btnText.textContent;
        const formData = new FormData(formEl);
        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionIds = [];

        cartItemsComponents.forEach(function(item) {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            sectionIds.push(item.dataset.sectionId);
          }
        });

        if (sectionIds.length) {
          formData.set('sections', sectionIds.join(','));
        }

        formData.set('sections_url', window.location.pathname);
        formEl.dataset.loading = 'true';
        addBtn.disabled = true;
        btnText.textContent = 'Adding...';

        fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json'
          },
          body: formData
        })
          .then(function(response) {
            return response.json().then(function(body) {
              if (!response.ok || body.status) {
                throw new Error(body.description || body.message || 'Unable to add this item to your cart.');
              }

              return body;
            });
          })
          .then((response) => {
            dispatchCartUpdate(response, formData);
            this.close();
            window.setTimeout(openCartDrawer, 160);
          })
          .catch(function(error) {
            console.error(error);
          })
          .finally(function() {
            formEl.dataset.loading = 'false';
            btnText.textContent = originalButtonText;

            const activeVariant = findVariant();
            if (activeVariant && activeVariant.available) {
              addBtn.disabled = false;
            }
          });
      });

      // Preselect a specific variant (e.g. a card pinned to a variant) by marking
      // its option buttons selected, then syncing price/variant via updateVariant.
      function preselectVariant(variantId) {
        const target = variants.find(function(v) { return String(v.id) === String(variantId); });
        if (!target) { updateVariant(); return; }

        const targetOptions = [target.option1, target.option2, target.option3];
        root.querySelectorAll('[data-sales-modal-option]').forEach(function(button) {
          const optionIndex = button.dataset.optionIndex;
          if (optionIndex == null) return;
          const wantedValue = targetOptions[Number(optionIndex)];
          const isMatch = button.dataset.value === wantedValue;
          button.classList.toggle('selected', isMatch);
          if (isMatch) {
            selectedOptions[optionIndex] = wantedValue;
            const labelEl = root.querySelector('#pwc-sel-' + cssEscape(sectionInstance) + '-' + optionIndex);
            if (labelEl) labelEl.textContent = wantedValue;
          }
        });

        updateVariant();
      }

      removeDuplicateRechargeBlock();

      if (preselectVariantId) {
        preselectVariant(preselectVariantId);
      } else {
        updateVariant();
      }
    }
  }

  function parseJSONScript(script, fallback) {
    if (!script) return fallback;

    try {
      return JSON.parse(script.textContent || '');
    } catch (error) {
      console.error('Unable to parse sales product modal JSON.', error);
      return fallback;
    }
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  // ---------------------------------------------------------------------------
  // Direct add-to-cart for single-option product cards (no modal).
  // When a sales-product-grid has the modal enabled AND "add to cart directly
  // when product has no options" enabled, cards for products with only the
  // default variant render with [data-sales-product-add-to-cart] instead of the
  // modal trigger. Clicking the .product-card-button adds the single variant
  // straight to the cart and opens the cart drawer, mirroring the modal's own
  // add-to-cart behaviour; clicking anywhere else on the card follows the
  // wrapper's link to the product page.
  // ---------------------------------------------------------------------------
  function salesDirectAddCartUrl() {
    return window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url
      ? window.Theme.routes.cart_add_url
      : '/cart/add.js';
  }

  function salesDirectOpenCartDrawer() {
    const cartDrawer =
      document.querySelector('#header-component .wc-header-cart-source cart-drawer-component') ||
      document.querySelector('#header-component cart-drawer-component') ||
      document.querySelector('cart-drawer-component');

    if (!cartDrawer) return;

    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    const cartTrigger = cartDrawer.querySelector('[data-testid="cart-drawer-trigger"]');
    if (cartTrigger instanceof HTMLElement) {
      cartTrigger.click();
    }
  }

  function salesDirectAddToCart(trigger) {
    const variantId = trigger.dataset.salesVariantId;
    if (!variantId || trigger.dataset.salesAdding === 'true') return;

    const button = trigger.querySelector('.product-card-button');
    const originalText = button ? button.textContent : '';

    trigger.dataset.salesAdding = 'true';
    if (button) button.textContent = 'Adding...';

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function(item) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length) {
      formData.set('sections', sectionIds.join(','));
    }
    formData.set('sections_url', window.location.pathname);

    fetch(salesDirectAddCartUrl(), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    })
      .then(function(response) {
        return response.json().then(function(body) {
          if (!response.ok || body.status) {
            throw new Error(body.description || body.message || 'Unable to add this item to your cart.');
          }
          return body;
        });
      })
      .then(function(response) {
        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: {
            resource: response || {},
            sourceId: String(variantId),
            data: {
              source: 'product-form-component',
              itemCount: 1,
              productId: trigger.dataset.salesProductId || '',
              variantId: String(variantId),
              sections: response && response.sections ? response.sections : {}
            }
          }
        }));
        window.setTimeout(salesDirectOpenCartDrawer, 160);
      })
      .catch(function(error) {
        console.error(error);
      })
      .finally(function() {
        trigger.dataset.salesAdding = 'false';
        if (button) button.textContent = originalText;
      });
  }

  // A modal-only include keeps the PDP's existing cart submission behavior.
  const modalOnly = document.currentScript?.hasAttribute('data-sales-modal-only');
  if (!window.salesProductDirectAddInitialized && !modalOnly) {
    window.salesProductDirectAddInitialized = true;

    document.addEventListener('click', function(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const trigger = target.closest('[data-sales-product-add-to-cart]');
      if (!trigger) return;

      // Only the add-to-cart button adds straight to cart. A click anywhere else
      // on the card is left alone so the wrapper's link navigates to the product
      // page. (.product-card-button only ever lives inside the trigger, so a match
      // necessarily belongs to this card.)
      if (!target.closest('.product-card-button')) return;

      event.preventDefault();
      salesDirectAddToCart(trigger);
    });
  }

  if (!customElements.get('sales-product-modal')) {
    customElements.define('sales-product-modal', SalesProductModal);
  }
})();
